import React, { useState, useEffect } from 'react';
import { IconDHTLogo } from './icons/IconPack';

interface NavbarProps {
  language: string;
  onLanguageChange: (lang: string) => void;
}

const Navbar: React.FC<NavbarProps> = ({ language, onLanguageChange }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const navLinks = [
    { name: language === 'zh' ? '公司簡介' : 'Company Profile', href: '#company-profile' },
    { name: language === 'zh' ? '解決方案' : 'Solutions', href: '#products' },
    { name: language === 'zh' ? 'AI 演示場' : 'AI Playgrounds', href: '#playground' },
    { name: language === 'zh' ? '客戶見證' : 'Testimonials', href: '#testimonials' },
    { name: language === 'zh' ? '工作機會' : 'Careers', href: '#careers' },
    { name: language === 'zh' ? '聯絡我們' : 'Contact', href: '#contact' }
  ];

  return (
    <nav className={`fixed w-full px-4 py-4 pb-3 transition-standard z-50 ${
      scrolled ? 'bg-white shadow-md' : 'bg-transparent'
    }`}>
      <div className="max-w-6xl mx-auto flex justify-between items-center">
        <a href="#" className="text-2xl font-bold text-blue-900 flex items-center transition-standard mr-18">
          <IconDHTLogo className="w-30 h-30 mr-2" />
          {language === 'zh' ? '迪衡數位科技' : 'DHT Solution'}
          <span className="ml-1 w-2 h-2 bg-blue-600 rounded-full animate-pulse"></span>
        </a>
        {/* Desktop menu */}
        <div className="hidden lg:flex items-center space-x-8">
          {navLinks.map((link, index) => (
            <a 
              key={index}
              href={link.href} 
              className="text-body-md font-medium text-gray-800 hover:text-blue-600 transition-standard py-2"
            >
              {link.name}
            </a>
          ))}
          {/* Language Toggle */}
          <div className="flex items-center space-x-2 ml-6">
            <button
              className={`px-2 py-1 rounded ${language === 'en' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-800'}`}
              onClick={() => onLanguageChange('en')}
            >
              EN
            </button>
            <button
              className={`px-2 py-1 rounded ${language === 'zh' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-800'}`}
              onClick={() => onLanguageChange('zh')}
            >
              中文
            </button>
          </div>
        </div>
        {/* Mobile menu button */}
        <div className="lg:hidden">
          <button 
            className="text-blue-900 p-2 focus:outline-none"
            onClick={toggleMobileMenu}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
          >
            {mobileMenuOpen ? (
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-6 h-6">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-6 h-6">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>
      </div>
      {/* Mobile menu dropdown */}
      <div 
        className={`lg:hidden absolute left-0 right-0 bg-white shadow-lg transition-standard transform origin-top ${
          mobileMenuOpen ? 'opacity-100 scale-y-100' : 'opacity-0 scale-y-0 pointer-events-none'
        }`}
      >
        <div className="p-4 max-w-6xl mx-auto">
          <ul className="flex flex-col space-y-4 py-4">
            {navLinks.map((link, index) => (
              <li key={index}>
                <a 
                  href={link.href} 
                  className="text-body-md font-medium text-gray-800 hover:text-blue-600 transition-standard block py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.name}
                </a>
              </li>
            ))}
            {/* Language Toggle in mobile menu */}
            <li>
              <div className="flex items-center space-x-2 mt-2">
                <button
                  className={`px-2 py-1 rounded ${language === 'en' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-800'}`}
                  onClick={() => onLanguageChange('en')}
                >
                  EN
                </button>
                <button
                  className={`px-2 py-1 rounded ${language === 'zh' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-800'}`}
                  onClick={() => onLanguageChange('zh')}
                >
                  中文
                </button>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;